/** @flow */
import React, { Component } from 'react';
import moment from 'moment';
import { withTheme } from 'styled-components';

import { BarChart, Bar, XAxis, YAxis, Legend, ResponsiveContainer, Brush, Tooltip, CartesianGrid } from 'recharts';
import { translate } from 'react-i18next';

import MetricRechartsTooltip from './tooltip/MetricRechartsTooltip';


type Props = {
  data: ?Object;
  isCompared: ?boolean;
  metricName: string;
  fromTime?: moment;
  toTime?: moment;
  converter?: (number) => number;
  dataRenderer?: (o: any) => string;
  disableAnimations: ?boolean;
  hideBrush: boolean;

  theme: Object;
  t: any;
}

class IntervalGraph extends Component<Props, any> {
  shouldComponentUpdate(nextProps: Props, nextState: any) {
    return this.props.converter !== nextProps.converter || this.props.data !== nextProps.data;
  }

  convertReachForChart = (props: Props) => {
    const converter = props.converter || ((n: number) => n);
    const dataRenderer = props.dataRenderer || (o => o);

    const data = props.data || {};
    const convertedData = Object.keys(data).sort().reduce((acc, key) => {
      const value: Object = { name: dataRenderer(key) };
      if (!props.isCompared) {
        value[props.metricName] = converter(data[key]);
        acc.push(value);
        return acc;
      }

      const current = converter(data[key].current);
      const compared = converter(data[key].compared);

      const difference = current - compared;

      const isDecreasing = difference < 0;
      value[props.metricName] = isDecreasing ? current : current - Math.abs(difference);
      value[isDecreasing ? props.t('decrease') : props.t('increase')] = Math.abs(difference);

      acc.push(value);
      return acc;
    }, []);

    return convertedData;
  };

  convertDataForTooltips = (props: Props) => {
    const converter = props.converter || (n => n);
    const dataRenderer = props.dataRenderer || (o => o);
    const data = props.data || {};

    return Object.keys(data).sort().reduce((acc, key) => {
      const renderedKey = dataRenderer(key);
      if (!props.isCompared) {
        acc[renderedKey] = converter(data[key]);
        return acc;
      }

      const current = converter(data[key].current);
      const compared = converter(data[key].compared);
      acc[renderedKey] = { current, compared };

      return acc;
    }, {});
  };

  render() {
    const { t, disableAnimations, hideBrush } = this.props;
    const { colors } = this.props.theme;

    const times = Object.keys(this.props.data || {});
    const fromTime = this.props.fromTime ? this.props.fromTime.format('HH:mm') : '5:00';
    const toTime = this.props.toTime ? this.props.toTime.format('HH:mm') : '21:00';

    const startAt = times.findIndex(i => i.trim() === fromTime);
    const endAt = times.findIndex(i => i.trim() === toTime);

    const data = this.convertDataForTooltips(this.props);
    return (
      <ResponsiveContainer>
        <BarChart data={this.convertReachForChart(this.props)}>
          <CartesianGrid stroke="#ddd" strokeDasharray="5 5" vertical={false} />
          <XAxis dataKey="name" />
          <YAxis width={70} height={30} />
          <Tooltip content={<MetricRechartsTooltip data={data} dataKey={this.props.metricName} />} />
          <Legend
            wrapperStyle={{ top: -15 }}
            verticalAlign="top"
            iconSize={30}
            height={20}
          />
          {times.length && !hideBrush && (
            <Brush
              startIndex={startAt !== -1 ? startAt : 0}
              endIndex={endAt !== -1 ? endAt : times.length - 1}
              dataKey="name"
              height={25}
              stroke={colors.primary}
            />
          )}
          <Bar
            dataKey={this.props.metricName}
            stackId="a"
            fill={colors.primary}
            legendType="none"
            animationEasing="ease-in-out"
            animationDuration={1000}
            isAnimationActive={!disableAnimations}
          />
          <Bar
            dataKey={t('decrease')}
            stackId="a"
            fill={colors.decreaseStats}
            legendType={this.props.isCompared ? 'circle' : 'none'}
            animationEasing="ease-in-out"
            animationDuration={1000}
            isAnimationActive={!disableAnimations}
          />
          <Bar
            dataKey={t('increase')}
            stackId="a"
            fill={colors.increaseStats}
            legendType={this.props.isCompared ? 'circle' : 'none'}
            animationEasing="ease-in-out"
            animationDuration={1000}
            isAnimationActive={!disableAnimations}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
}

export default withTheme(translate()(IntervalGraph));



// WEBPACK FOOTER //
// ./src/common/components/charts/IntervalGraph.js